import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Slider } from "@mui/material";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";

interface PPCMCalibrationPanelTranslations {
  title?: string;
  description?: string;
  instruction?: string;
  dragHint?: string;
  smallerLogo?: string;
  largerLogo?: string;
  back?: string;
  confirmContinue?: string;
}

interface PPCMCalibrationPanelProps {
  imageFile: File;
  onCalibrated: (ppcm: number) => void;
  onBack: () => void;
  initialPpcm?: number;
  translations?: PPCMCalibrationPanelTranslations;
}

export function PPCMCalibrationPanel({
  imageFile,
  onCalibrated,
  onBack,
  initialPpcm = 22,
  translations = {},
}: PPCMCalibrationPanelProps): React.ReactElement {
  const { t: translate } = useTranslation();
  const defaultCalibrationTranslations: PPCMCalibrationPanelTranslations = {
    title: translate("designListStyle.calibrateLogoSize"),
    description: translate("designListStyle.calibrationDescription"),
    instruction: translate("designListStyle.calibrationInstruction"),
    dragHint: translate("designListStyle.calibrationDragHint"),
    smallerLogo: translate("designListStyle.smallerLogo"),
    largerLogo: translate("designListStyle.largerLogo"),
    back: translate("back"),
    confirmContinue: translate("designListStyle.confirmAndContinue"),
  };
  const t = { ...defaultCalibrationTranslations, ...translations };
  const [ppcm, setPpcm] = useState(initialPpcm);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [displayScale, setDisplayScale] = useState(1);
  const [boxPosition, setBoxPosition] = useState({ x: 50, y: 50 }); // percentage position
  const [isDragging, setIsDragging] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Create object URL for preview and get image dimensions
  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    setImageUrl(url);

    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Calculate the actual rendered image dimensions.
  // We cannot rely on clientWidth because object-contain may render the image
  // smaller than the element box (e.g. when max-height constrains a tall image).
  // Instead, compute the rendered size from the container and natural aspect ratio,
  // matching how the design editor calculates its scaled view.
  function computeDisplayScale() {
    if (!imageRef.current || imageDimensions.width <= 0 || imageDimensions.height <= 0) return;

    const elementWidth = imageRef.current.clientWidth;
    const elementHeight = imageRef.current.clientHeight;
    const naturalAspect = imageDimensions.width / imageDimensions.height;
    const elementAspect = elementWidth / elementHeight;

    // object-contain: image fits within the element box maintaining aspect ratio
    let renderedWidth: number;
    if (naturalAspect > elementAspect) {
      // Image is wider relative to element — constrained by width
      renderedWidth = elementWidth;
    } else {
      // Image is taller relative to element — constrained by height
      renderedWidth = elementHeight * naturalAspect;
    }

    setDisplayScale(renderedWidth / imageDimensions.width);
  }

  // Calculate display scale when image loads
  useEffect(() => {
    computeDisplayScale();
  }, [imageDimensions, imageUrl]);

  // Recalculate on window resize
  useEffect(() => {
    window.addEventListener("resize", computeDisplayScale);
    return () => window.removeEventListener("resize", computeDisplayScale);
  }, [imageDimensions]);

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return;

    function handleMouseMove(e: MouseEvent) {
      if (!imageContainerRef.current) return;

      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp to container bounds
      setBoxPosition({
        x: Math.max(5, Math.min(95, x)),
        y: Math.max(5, Math.min(95, y)),
      });
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Calculate reference box size (10cm = 100mm)
  // Reference box represents 10cm at current PPCM, scaled for display
  const referenceBoxWidthPx = 10 * ppcm * displayScale; // 10cm * ppcm pixels/cm * display scale
  const referenceBoxHeightPx = 10 * ppcm * displayScale; // 2cm height

  return (
    <div className="flex flex-col gap-4 p-6 min-w-[700px] max-w-[900px]">
      <h2 className="text-lg font-bold">{t.title}</h2>
      <p className="text-sm text-gray-500">{t.description}</p>

      {/* Image with overlay */}
      <div
        ref={imageContainerRef}
        className="relative border rounded-lg overflow-hidden bg-gray-100"
      >
        {imageUrl && (
          <img
            ref={imageRef}
            src={imageUrl}
            alt={translate("designListStyle.uploadedGarmentAlt")}
            className="w-full h-auto max-h-[600px] object-contain"
            onLoad={() => {
              // Recalculate scale after image loads
              computeDisplayScale();
            }}
          />
        )}

        {/* Instruction overlay at top of image */}
        <div className="absolute top-0 left-0 right-0 bg-black/70 text-white px-4 py-3 text-center">
          <p className="text-sm font-base">{t.instruction}</p>
          <p className="text-xs text-gray-300 mt-1">{t.dragHint}</p>
        </div>

        {/* Draggable reference box overlay */}
        {displayScale > 0 && (
          <div
            ref={boxRef}
            className={`absolute border-2 border-blue-500 bg-blue-500/20 ${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            style={{
              width: `${referenceBoxWidthPx}px`,
              height: `${referenceBoxHeightPx}px`,
              left: `${boxPosition.x}%`,
              top: `${boxPosition.y}%`,
              transform: "translate(-50%, -50%)",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
          />
        )}
      </div>

      {/* Slider */}
      <div className="px-2">
        <Slider
          value={ppcm}
          onChange={(_, value) => setPpcm(value as number)}
          min={1}
          max={100}
          step={0.5}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${value}`}
          sx={{
            color: "#3b82f6",
            "& .MuiSlider-thumb": {
              backgroundColor: "#3b82f6",
            },
            "& .MuiSlider-track": {
              backgroundColor: "#3b82f6",
            },
            "& .MuiSlider-rail": {
              backgroundColor: "#d1d5db",
            },
          }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{t.smallerLogo}</span>
          <span>{t.largerLogo}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 mt-2">
        <button
          onClick={onBack}
          className="text-sm text-gray-600 hover:text-gray-800 underline"
        >
          {t.back}
        </button>
        <PrimaryButton onClick={() => onCalibrated(ppcm)}>
          {t.confirmContinue}
        </PrimaryButton>
      </div>
    </div>
  );
}
